// frontend/app/components/lessonPlans/LessonDownloadButton.js
//
// Icon button that downloads one lesson as <slug>.json, in the shape the
// upload accepts. Web only (it saves through an <a download>). A failure is
// shown as a line of text beside the button, never an alert.

import React, { useState } from "react";
import { Platform, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { apiExportLessonPlan } from "../../lessonPlans";
import { saveJsonFile } from "../../utils/downloadJsonFile";

const identity = (value) => value;

const fileNameFor = (doc, lessonPlanId) =>
  typeof doc?.slug === "string" && doc.slug ? `${doc.slug}.json` : `lesson-${lessonPlanId}.json`;

const LessonDownloadButton = ({
  fetchWithAuth,
  lessonPlanId,
  color = "#64748b",
  borderColor = "#cbd5e1",
  t = identity,
  style,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (Platform.OS !== "web" || typeof fetchWithAuth !== "function" || !lessonPlanId) {
    return null;
  }

  const download = async () => {
    setError(null);
    setBusy(true);
    try {
      const doc = await apiExportLessonPlan(fetchWithAuth, lessonPlanId);
      saveJsonFile(JSON.stringify(doc, null, 2), fileNameFor(doc, lessonPlanId));
    } catch (err) {
      setError(err?.message || t("The lesson could not be downloaded."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={[styles.wrap, style]}>
      <TouchableOpacity
        onPress={download}
        disabled={busy}
        accessibilityRole="button"
        accessibilityLabel={t("Download JSON")}
        style={[styles.button, { borderColor, opacity: busy ? 0.5 : 1 }]}
      >
        <MaterialCommunityIcons name="download-outline" size={16} color={color} />
      </TouchableOpacity>
      {error ? (
        <Text testID="lesson-download-error" style={styles.error} accessibilityRole="alert">
          {error}
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { alignItems: "flex-end", gap: 4 },
  button: {
    width: 32,
    height: 32,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  error: { fontSize: 11, color: "#b91c1c", maxWidth: 220, textAlign: "right" },
});

export default LessonDownloadButton;
